import { FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement } from '@micromag/core/components';
import { useScreenSize, useViewerContext, useViewerWebView, usePlaybackContext, usePlaybackMediaRef, useScreenRenderContext } from '@micromag/core/contexts';
import { useTrackScreenEvent, useDimensionObserver } from '@micromag/core/hooks';
import { isTextFilled, isHeaderFilled, isFooterFilled, getFooterProps } from '@micromag/core/utils';
import Background from '@micromag/element-background';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Heading from '@micromag/element-heading';
import Layout from '@micromag/element-layout';
import Scroll from '@micromag/element-scroll';
import Text from '@micromag/element-text';

var styles = {"container":"micromag-screen-ranking-container","background":"micromag-screen-ranking-background","empty":"micromag-screen-ranking-empty","emptyTitle":"micromag-screen-ranking-emptyTitle","content":"micromag-screen-ranking-content","item":"micromag-screen-ranking-item","rankText":"micromag-screen-ranking-rankText","itemTitle":"micromag-screen-ranking-itemTitle","description":"micromag-screen-ranking-description","callToAction":"micromag-screen-ranking-callToAction","disabled":"micromag-screen-ranking-disabled","sideLayout":"micromag-screen-ranking-sideLayout","elementsContainer":"micromag-screen-ranking-elementsContainer","rank":"micromag-screen-ranking-rank","label":"micromag-screen-ranking-label","isPlaceholder":"micromag-screen-ranking-isPlaceholder","scroll":"micromag-screen-ranking-scroll"};

var propTypes = {
  layout: PropTypes.oneOf(['side', 'over']),
  title: PropTypes$1.headingElement,
  items: PropTypes.arrayOf(PropTypes$1.textElement),
  itemTitleStyle: PropTypes$1.textStyle,
  itemDescriptionStyle: PropTypes$1.textStyle,
  numbersStyle: PropTypes$1.textStyle,
  ascending: PropTypes.bool,
  spacing: PropTypes.number,
  background: PropTypes$1.backgroundElement,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  current: PropTypes.bool,
  preload: PropTypes.bool,
  type: PropTypes.string,
  className: PropTypes.string
};
var defaultProps = {
  layout: 'side',
  title: null,
  items: [null],
  itemTitleStyle: null,
  itemDescriptionStyle: null,
  numbersStyle: null,
  ascending: false,
  spacing: 20,
  background: null,
  header: null,
  footer: null,
  current: true,
  preload: true,
  type: null,
  className: null
};
var RankingScreen = function RankingScreen(_ref) {
  var layout = _ref.layout,
    title = _ref.title,
    items = _ref.items,
    itemTitleStyle = _ref.itemTitleStyle,
    itemDescriptionStyle = _ref.itemDescriptionStyle,
    numbersStyle = _ref.numbersStyle,
    ascending = _ref.ascending,
    spacing = _ref.spacing,
    background = _ref.background,
    header = _ref.header,
    footer = _ref.footer,
    current = _ref.current,
    preload = _ref.preload,
    type = _ref.type,
    className = _ref.className;
  var trackScreenEvent = useTrackScreenEvent(type);
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var _usePlaybackContext = usePlaybackContext(),
    muted = _usePlaybackContext.muted;
  var mediaRef = usePlaybackMediaRef(current);
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit,
    isStatic = _useScreenRenderConte.isStatic,
    isCapture = _useScreenRenderConte.isCapture;
  var finalItems = isPlaceholder ? _toConsumableArray(new Array(10)).map(function () {
    return {};
  }) : items || [null];
  var itemsCount = finalItems !== null ? finalItems.length : 0;
  var transitionDisabled = isStatic || isCapture || isPlaceholder || isPreview || isEdit;
  var scrollingDisabled = !isEdit && transitionDisabled || !current;
  var backgroundPlaying = current && (isView || isEdit);
  var mediaShouldLoad = current || preload;
  var hasTitle = isTextFilled(title);
  var titleElement = /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "Title",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "BSTWf8",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    }),
    emptyClassName: classNames([styles.empty, styles.emptyTitle]),
    isEmpty: !hasTitle
  }, hasTitle ? /*#__PURE__*/React.createElement(Heading, Object.assign({
    className: styles.title
  }, title)) : null);
  var elements = (finalItems || []).map(function (item, itemI) {
    var _ref2 = item || {},
      _ref2$title = _ref2.title,
      itemTitle = _ref2$title === void 0 ? null : _ref2$title,
      _ref2$description = _ref2.description,
      description = _ref2$description === void 0 ? null : _ref2$description;
    var hasItemTitle = isTextFilled(itemTitle);
    var hasDescription = isTextFilled(description);
    var _ref3 = itemTitle || {},
      _ref3$textStyle = _ref3.textStyle,
      titleTextStyle = _ref3$textStyle === void 0 ? null : _ref3$textStyle;
    var _ref4 = description || {},
      _ref4$textStyle = _ref4.textStyle,
      descriptionTextStyle = _ref4$textStyle === void 0 ? null : _ref4$textStyle;
    var itemTitleElement = /*#__PURE__*/React.createElement("div", {
      className: styles.itemTitle
    }, /*#__PURE__*/React.createElement(ScreenElement, {
      placeholder: "title",
      emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "bmO4Ss",
        defaultMessage: [{
          "type": 0,
          "value": "Entry Title"
        }]
      }),
      emptyClassName: styles.empty,
      isEmpty: !hasItemTitle
    }, hasItemTitle ? /*#__PURE__*/React.createElement(Heading, Object.assign({}, itemTitle, {
      textStyle: _objectSpread(_objectSpread({}, itemTitleStyle || null), titleTextStyle)
    })) : null));
    var descriptionElement = /*#__PURE__*/React.createElement("div", {
      className: styles.description
    }, /*#__PURE__*/React.createElement(ScreenElement, {
      placeholder: "text",
      emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "507VAi",
        defaultMessage: [{
          "type": 0,
          "value": "Description"
        }]
      }),
      emptyClassName: styles.empty,
      isEmpty: !hasDescription
    }, hasDescription ? /*#__PURE__*/React.createElement(Text, Object.assign({}, description, {
      textStyle: _objectSpread(_objectSpread({}, itemDescriptionStyle || null), descriptionTextStyle)
    })) : null));
    var rankText = "".concat(ascending ? itemI + 1 : itemsCount - itemI);
    return /*#__PURE__*/React.createElement("div", {
      className: styles.item,
      key: "item-".concat(itemI)
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.rank
    }, isPlaceholder ? rankText : /*#__PURE__*/React.createElement(Text, {
      className: styles.rankText,
      body: rankText,
      textStyle: numbersStyle
    })), /*#__PURE__*/React.createElement("div", {
      className: styles.label
    }, itemTitleElement, descriptionElement));
  });

  // Call to Action

  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    scrolledBottom = _useState2[0],
    setScrolledBottom = _useState2[1];
  var _useDimensionObserver = useDimensionObserver(),
    footerRef = _useDimensionObserver.ref,
    _useDimensionObserver2 = _useDimensionObserver.height,
    foterHeight = _useDimensionObserver2 === void 0 ? 0 : _useDimensionObserver2;
  var onScrolledBottom = useCallback(function (_ref5) {
    var initial = _ref5.initial;
    if (initial) {
      trackScreenEvent('scroll', 'Screen');
    }
    setScrolledBottom(true);
  }, [trackScreenEvent]);
  var onScrolledNotBottom = useCallback(function () {
    setScrolledBottom(false);
  }, [setScrolledBottom]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty(_defineProperty({}, className, className !== null), styles["".concat(layout, "Layout")], layout !== null), styles.isPlaceholder, isPlaceholder)]),
    "data-screen-ready": true
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, /*#__PURE__*/React.createElement(Scroll, {
    className: styles.scroll,
    verticalAlign: "middle",
    disabled: scrollingDisabled,
    onScrolledBottom: onScrolledBottom,
    onScrolledNotBottom: onScrolledNotBottom,
    withShadow: true
  }, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    style: {
      paddingTop: spacing / 2 + viewerTopHeight,
      paddingLeft: spacing,
      paddingRight: spacing
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null, /*#__PURE__*/React.createElement(Layout, {
    className: styles.layout,
    style: !isPlaceholder ? {
      padding: spacing,
      paddingTop: !isPlaceholder && hasHeader ? spacing : (!isPreview ? viewerTopHeight : 0) + spacing,
      paddingBottom: (current && !isPreview ? viewerBottomHeight : 0) + (foterHeight || spacing)
    } : null
  }, titleElement, /*#__PURE__*/React.createElement("div", {
    className: styles.elementsContainer
  }, elements))), !isPlaceholder && hasFooter ? /*#__PURE__*/React.createElement("div", {
    ref: footerRef,
    className: classNames([styles.callToAction, _defineProperty({}, styles.disabled, !scrolledBottom)]),
    style: {
      transform: current && !isPreview ? "translate(0, -".concat(viewerBottomHeight, "px)") : null,
      paddingLeft: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingRight: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingTop: spacing / 2,
      paddingBottom: spacing / 2
    }
  }, /*#__PURE__*/React.createElement(Footer, footerProps)) : null), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: mediaShouldLoad,
    mediaRef: mediaRef,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
RankingScreen.propTypes = propTypes;
RankingScreen.defaultProps = defaultProps;

// import * as transforms from './transforms/index';

var definition = {
  id: 'ranking',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "cGItoy",
      defaultMessage: [{
        "type": 0,
        "value": "List"
      }]
    }),
    order: 8
  },
  title: defineMessage({
    id: "17BMn2",
    defaultMessage: [{
      "type": 0,
      "value": "Ranking"
    }]
  }),
  component: RankingScreen,
  layouts: ['side', 'over'],
  // transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'side',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'title',
    type: 'heading-element',
    // inline: true,
    theme: {
      textStyle: 'heading1'
    },
    label: defineMessage({
      id: "N25iDO",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    })
  }, {
    name: 'items',
    type: 'entries',
    theme: {
      title: {
        textStyle: 'heading2'
      },
      description: {
        textStyle: 'text'
      }
    },
    label: defineMessage({
      id: "sESmah",
      defaultMessage: [{
        "type": 0,
        "value": "Entries"
      }]
    })
  }, {
    type: 'fields',
    label: defineMessage({
      id: "xac4z/",
      defaultMessage: [{
        "type": 0,
        "value": "Entry styles"
      }]
    }),
    isList: true,
    fields: [{
      name: 'itemTitleStyle',
      type: 'text-style-form',
      label: defineMessage({
        id: "+AEVbJ",
        defaultMessage: [{
          "type": 0,
          "value": "Title"
        }]
      })
    }, {
      name: 'itemDescriptionStyle',
      type: 'text-style-form',
      label: defineMessage({
        id: "ZCe0r4",
        defaultMessage: [{
          "type": 0,
          "value": "Description"
        }]
      })
    }, {
      name: 'numbersStyle',
      type: 'text-style-form',
      theme: {
        textStyle: 'heading1'
      },
      label: defineMessage({
        id: "UdrdRF",
        defaultMessage: [{
          "type": 0,
          "value": "Numbers style"
        }]
      })
    }]
  }, {
    name: 'ascending',
    type: 'toggle',
    label: defineMessage({
      id: "9DhYaZ",
      defaultMessage: [{
        "type": 0,
        "value": "Ascending"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
};

export { RankingScreen, definition as default };
